import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { generateClient } from 'aws-amplify/data';
import './IntakeForm.css';
import { isoToNYDateTimeLocal } from '../utils/datetime';

const client = generateClient({ authMode: 'userPool' });

// For datetime-local fields (e.g. courtDateTime): show the stored UTC instant
// as New York wall-clock time so the value the user edits matches court time.
const formatDateTimeLocal = (dateString) => isoToNYDateTimeLocal(dateString);

// Helper for date-only fields (e.g. violationDate, dob)
const formatDateOnly = (dateString) => {
  const date = new Date(dateString);
  if (isNaN(date)) return '';
  return date.toISOString().split('T')[0];
};

const EditCase = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { updateCase } = useAppContext();

  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [customDisposition, setCustomDisposition] = useState('');
  const [dispositionSelection, setDispositionSelection] = useState('');

  useEffect(() => {
    const loadCase = async () => {
      try {
        setLoading(true);
        const res = await client.models.Case.get({ id });
        const existing = res?.data;

        if (!existing) {
          navigate('/');
        } else {
          setFormData({
            ...existing,
            dob: existing.dob ? formatDateOnly(existing.dob) : '',
            courtDateTime: existing.courtDateTime ? formatDateTimeLocal(existing.courtDateTime) : '',
            violationDate: existing.violationDate ? formatDateOnly(existing.violationDate) : '',
            officerName: existing.officerName || '',
            totalFee: existing.totalFee?.toString() || '',
            paidAmount: existing.paidAmount?.toString() || '',
            amountDue: existing.amountDue?.toString() || '',
          });

          const predefinedDisposition = [
            'NEW', 'MISSED', 'Open', 'Waiting on Client', 'Pending', 'Closed', 'TBD',
            'OPEN', 'GUILTY', 'NOT GUILTY', 'UNHIRED', 'PLED', 'CLOSED'
          ];
          if (existing.status && predefinedDisposition.includes(existing.status)) {
            setDispositionSelection(existing.status);
            setCustomDisposition('');
          } else if (existing.status) {
            setDispositionSelection('Other');
            setCustomDisposition(existing.status);
          }
        }
      } catch (err) {
        console.error('❌ Error loading case for edit:', err);
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    loadCase();
  }, [id, navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleDispositionChange = (e) => {
    const value = e.target.value;
    setDispositionSelection(value);
    setFormData((prev) => ({
      ...prev,
      status: value === 'Other' ? customDisposition : value,
    }));
  };

  const handleCustomDispositionChange = (e) => {
    const value = e.target.value;
    setCustomDisposition(value);
    setFormData((prev) => ({
      ...prev,
      status: value,
    }));
  };

  const calculateAmountDue = () => {
    const total = parseFloat(formData.totalFee) || 0;
    const paid = parseFloat(formData.paidAmount) || 0;
    return (total - paid).toFixed(2);
  };

  const getBalanceStatus = () => {
    return calculateAmountDue() === '0.00' ? 'PAID IN FULL' : 'NOT PAID';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const updated = {
      ...formData,
      status: dispositionSelection === 'Other' ? customDisposition : dispositionSelection,
      disposition: undefined,
      totalFee: parseFloat(formData.totalFee) || 0,
      paidAmount: parseFloat(formData.paidAmount) || 0,
      amountDue: parseFloat(formData.amountDue) || 0,
      balanceDue: getBalanceStatus(),
      // Raw datetime-local value; interpreted as New York time in updateCase().
      courtDateTime: formData.courtDateTime || null,
      violationDate: formData.violationDate ? new Date(formData.violationDate).toISOString() : null,
    };

    delete updated.licenseNumber; // Ensure it's excluded if still present

    await updateCase(updated);
    navigate(`/case/${id}`, { state: { fromEdit: true } });
  };

  if (loading) return <div className="container">Loading case...</div>;

  return (
    <div className="container">
      <h2>Edit Case</h2>

      <form onSubmit={handleSubmit} className="intake-form">
        <div className="form-grid">
          <div>
            <label>First Name</label>
            <input name="firstName" value={formData.firstName || ''} onChange={handleChange} />
          </div>
          <div>
            <label>Ticket #</label>
            <input name="appearanceNumber" value={formData.appearanceNumber || ''} onChange={handleChange} />
          </div>
          <div>
            <label>Court Appearance Date & Time</label>
            <input type="datetime-local" name="courtDateTime" value={formData.courtDateTime || ''} onChange={handleChange} />
          </div>

          <div>
            <label>Last Name</label>
            <input name="lastName" value={formData.lastName || ''} onChange={handleChange} />
          </div>
          <div>
            <label>Officer Name</label>
            <input name="officerName" value={formData.officerName || ''} onChange={handleChange} />
          </div>
          <div>
            <label>County</label>
            <input name="incidentCounty" value={formData.incidentCounty || ''} onChange={handleChange} />
          </div>

          <div>
            <label>Phone</label>
            <input name="phone" value={formData.phone || ''} onChange={handleChange} />
          </div>
          <div>
            <label>Total Fee</label>
            <input type="number" name="totalFee" value={formData.totalFee} onChange={handleChange} />
          </div>
          <div>
            <label>Violation Type</label>
            <input name="caseType" value={formData.caseType || ''} onChange={handleChange} />
          </div>
        
          <div>
            <label>Secondary Phone</label>
            <input name="secondaryPhone" value={formData.secondaryPhone || ''} onChange={handleChange} />
          </div>
          <div>
            <label>Paid Amount</label>
            <input type="number" name="paidAmount" value={formData.paidAmount} onChange={handleChange} />
          </div>
          <div>
            <label>Violation Date</label>
            <input type="date" name="violationDate" value={formData.violationDate || ''} onChange={handleChange} />
          </div>
          <div>
            <label>Email</label>
            <input type="email" name="email" value={formData.email || ''} onChange={handleChange} />
          </div>
          
          <div>
            <label>Calculated Amount Due</label>
            <input type="text" value={`$${calculateAmountDue()}`} readOnly disabled style={{ backgroundColor: '#f0f0f0' }} />
          </div>

          <div>
            <label>Disposition</label>
            <select name="status" value={dispositionSelection} onChange={handleDispositionChange}>
              <option value="">Select Disposition</option>
              <option value="NEW">NEW</option>
              <option value="Open">Open</option>
              <option value="Waiting on Date">Waiting on Date</option>
              <option value="MISSED">MISSED</option>
              <option value="GUILTY">GUILTY</option>
              <option value="NOT GUILTY">NOT GUILTY</option>
              <option value="REDUCED">REDUCED</option>
              <option value="UNHIRED">UNHIRED</option>
              <option value="PLED">PLED</option>
              <option value="Closed">Closed</option>
              <option value="Other">Other</option>
            </select>
            {dispositionSelection === 'Other' && (
              <input
                type="text"
                placeholder="Enter custom disposition"
                value={customDisposition}
                onChange={handleCustomDispositionChange}
                style={{ marginTop: '0.5rem' }}
              />
            )}
          </div>

          <div>
            <label>Mailing Address</label>
            <input name="mailingAddress" value={formData.mailingAddress || ''} onChange={handleChange} />
          </div>
          <div>
            <label>Balance Due?</label>
            <input type="text" value={getBalanceStatus()} readOnly disabled style={{
              color: getBalanceStatus() === 'PAID IN FULL' ? 'green' : 'red',
              fontWeight: 'bold',
              backgroundColor: '#f0f0f0'
            }} />
          </div>
          <div>
            <label>ID#</label>
            <input name="idnumber" value={formData.idnumber || ''} onChange={handleChange} />
          </div>
          <div>
            <label>Date of Birth</label>
            <input type="date" name="dob" value={formData.dob || ''} onChange={handleChange} />
          </div>
          {/* Removed Driver’s License # field */}
          
          <div className="full-width">
            <label>Comments</label>
            <textarea name="comments" value={formData.comments || ''} onChange={handleChange} />
          </div>
        </div>

        <div className="form-actions">
          <button type="submit">Save Changes</button>
        </div>
      </form>

      <footer className="settings-footer">
        © {new Date().getFullYear()} Zbrella Inc. All rights reserved.
      </footer>
    </div>
  );
};

export default EditCase;
